/**
 * @file UsersFilters.tsx
 * @brief Search + role + status + native-language filter bar for Users.
 *
 * @details
 * Holds the input state internally so the parent only sees a debounced
 * filter object. Sentinel value `"all"` is the "no filter" option in the
 * role/status/billing menus. Native language uses `LanguagePicker` and
 * filters on the ISO base code stored in `users.native_language`.
 * Layout is flush `GridCells` for the section-grid shell (29-07-2026).
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — end-to-end GridCell filters
 * @updated 01-10-2026: language / role / status / billing became header
 *          pills (`UsersHeaderFilters`); no Clear button. This file's bar is
 *          the search.
 */

import { useEffect, useState } from "react";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import { LanguagePicker } from "@/components/ui/LanguagePicker";
import type { SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import type { AdminUsersListParams } from "../schemas";

const ALL = "all";

const ROLE_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any role" },
  { value: "user", label: "User" },
  { value: "admin", label: "Company Admin" },
  { value: "superadmin", label: "Super Admin" },
];

const STATUS_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any status" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "deleted", label: "Deleted" },
];

const BILLING_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any billing" },
  { value: "basic", label: "Pay as you go" },
  { value: "subscribed", label: "Any plan" },
  { value: "starter", label: "Starter" },
  { value: "pro", label: "Pro" },
  { value: "business", label: "Business" },
];

type RoleFilter = NonNullable<AdminUsersListParams["role"]> | typeof ALL;
type StatusFilter = NonNullable<AdminUsersListParams["status"]> | typeof ALL;
type BillingFilter = NonNullable<AdminUsersListParams["billing"]> | typeof ALL;

/** Locale or code from LanguagePicker → ISO base code for DB filter. */
function toNativeLanguageCode(value: string | null): string | undefined {
  if (!value) return undefined;
  const code = value.trim().toLowerCase().split("-")[0];
  return code || undefined;
}

const labelOf = (items: SearchableMenuItem[], v: string) =>
  items.find((i) => i.value === v)?.label ?? items[0].label;

interface UsersFiltersProps {
  value: AdminUsersListParams;
  onChange: (next: AdminUsersListParams) => void;
}

/** Language, role, status and billing as the page header's text pills. */
export function UsersHeaderFilters({ value, onChange }: UsersFiltersProps) {
  const role: RoleFilter = (value.role ?? ALL) as RoleFilter;
  const status: StatusFilter = (value.status ?? ALL) as StatusFilter;
  const billing: BillingFilter = (value.billing ?? ALL) as BillingFilter;

  return (
    <>
      <LanguagePicker
        variant="header"
        value={value.native_language}
        onChange={(id) =>
          onChange({
            ...value,
            native_language: toNativeLanguageCode(id),
            page: 1,
          })
        }
        placeholder="Any language"
      />
      <HeaderFilterMenu
        title="Filter by role"
        searchPlaceholder="Search roles"
        items={ROLE_ITEMS}
        selected={role}
        label={labelOf(ROLE_ITEMS, role)}
        active={role !== ALL}
        onClear={() => onChange({ ...value, role: undefined, page: 1 })}
        onSelect={(v) =>
          onChange({
            ...value,
            role: v === ALL ? undefined : (v as AdminUsersListParams["role"]),
            page: 1,
          })
        }
      />
      <HeaderFilterMenu
        title="Filter by status"
        searchPlaceholder="Search statuses"
        items={STATUS_ITEMS}
        selected={status}
        label={labelOf(STATUS_ITEMS, status)}
        active={status !== ALL}
        onClear={() => onChange({ ...value, status: undefined, page: 1 })}
        onSelect={(v) =>
          onChange({
            ...value,
            status: v === ALL ? undefined : (v as AdminUsersListParams["status"]),
            page: 1,
          })
        }
      />
      <HeaderFilterMenu
        title="Filter by billing"
        searchPlaceholder="Search billing"
        items={BILLING_ITEMS}
        selected={billing}
        label={labelOf(BILLING_ITEMS, billing)}
        active={billing !== ALL}
        onClear={() => onChange({ ...value, billing: undefined, page: 1 })}
        onSelect={(v) =>
          onChange({
            ...value,
            billing: v === ALL ? undefined : (v as AdminUsersListParams["billing"]),
            page: 1,
          })
        }
      />
    </>
  );
}

/** The search, rendered by the page inside its bottom dock (DES 01-10-2026). */
export function UsersFilters({ value, onChange }: UsersFiltersProps) {
  // ----------------------------------------------------------------------
  // Local input state — kept here so typing doesn't fire a query per
  // keystroke. The debounced value is what gets pushed up to the parent.
  // ----------------------------------------------------------------------
  const [searchInput, setSearchInput] = useState(value.search ?? "");
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    if ((value.search ?? "") === debouncedSearch) return;
    onChange({ ...value, search: debouncedSearch || undefined, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    // The Notes screen's search (DES 01-10-2026), rendered by the page
    // inside its bottom PageDock ("dock style ki tarha neechay").
    <ExpandingSearch
      value={searchInput}
      onChange={(next) => setSearchInput(next)}
      placeholder="Search by user ID, name, or email"
      label="Search users"
    />
  );
}
